import React, { ReactElement, ReactNode } from 'react';

import { AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import ClaimFlowType from 'types/ClaimFlowType';

import { UnderlinedBoldGhostButton } from 'chime-ui/components/Button';
import { useBottomSheet } from 'chime-ui/components/Modal';
import { Text, TextBlock, Title } from 'chime-ui/components/Typography';
import { colors } from 'chime-ui/system/colors';
import media from 'chime-ui/system/media';

import Layout from 'components/Layout';
import Header from 'components/Layout/Header';

import { useUserContext } from 'hooks/useUserContext';

import TermsAndConditions from './TermsAndConditions';
import useLandingBase from './useLandingBase';

type LandingBaseProps = {
  pageLocation: string;
  claimFlowType: ClaimFlowType;
  landingTitle?: ReactElement;
  children: ReactNode;
  showHeaderLogin?: boolean;
};

function LandingBase({
  pageLocation,
  claimFlowType,
  landingTitle,
  children,
  showHeaderLogin = true,
}: LandingBaseProps): ReactElement {
  const [userData] = useUserContext();
  const { bank, memo, claimWithChime: isClaimWithChimeEnabled } = userData;

  const { onFAQClick, gif } = useLandingBase(pageLocation);

  const { Modal, isShowing, handleDismiss } = useBottomSheet();

  return (
    <Layout header={<Header showLogin={showHeaderLogin} showTagline={true} />}>
      <TitleLayout hasMemoGif={!!gif}>
        <PlainTextTitleLayout>{landingTitle}</PlainTextTitleLayout>
        {memo && <Text>For: {memo}!</Text>}
        {gif && (
          <GifLayout>
            <Gif
              aria-label={'Memo Gif'}
              alt={gif?.title}
              src={gif?.images?.original.url}
            />
          </GifLayout>
        )}
      </TitleLayout>

      {children}

      <AnimatePresence>
        {isShowing && (
          <Modal key="modal" handleDismiss={handleDismiss}>
            <ModalTitle>
              <Title size={'small'}>What&apos;s Chime?</Title>
            </ModalTitle>
            <TextBlock size={'standard'}>
              Chime is a mobile and online bank account with an award-winning
              app that allows you to manage your banking on the go.
            </TextBlock>
            <br />
            <Text size={'standard'}>
              Visit{' '}
              <UnderlinedBoldGhostButton
                as="a"
                href="https://chime.com"
                target="_blank"
                rel="noreferrer"
              >
                Chime.com
              </UnderlinedBoldGhostButton>
            </Text>
          </Modal>
        )}
      </AnimatePresence>

      <FinePrint>
        <TermsAndConditions
          bank={bank}
          claimFlowType={claimFlowType}
          onFAQClick={onFAQClick}
          isBonusTextVisible={isClaimWithChimeEnabled}
        />
        {gif && (
          <TextBlock size={'small'} color={colors.darkGrey}>
            Powered by GIPHY
          </TextBlock>
        )}
      </FinePrint>
    </Layout>
  );
}

const PlainTextTitleLayout = styled.div`
  padding-bottom: 0.5rem;
`;

const GifLayout = styled.div`
  padding-top: 1rem;
`;

const Gif = styled.img`
  border-radius: 1rem;
  max-height: 110px;
  max-width: 100%;

  ${media.tablet`
    max-height: 300px;
  `}
`;

const TitleLayout = styled.div<{ hasMemoGif: boolean }>`
  display: flex;
  justify-content: center;
  align-content: center;
  flex-direction: column;
  text-align: center;
  margin: ${({ hasMemoGif }) =>
    hasMemoGif ? '70px 0 1.5rem 0' : '70px 0 90px 0'};

  ${media.mobileOnly`
      margin: 3.5rem 0
    `}
`;

const ModalTitle = styled.div`
  padding-right: 1.5rem;
  min-height: 1.75rem;
  margin-bottom: 0.5rem;
`;

const FinePrint = styled.div`
  padding: 2rem 0 0;
`;

export default LandingBase;
